import { DevConfig } from '../../config/environment/devConfig';
import { Helix2000Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { PreloadScene } from '../scenes/PreloadScene';
import { BootService } from './BootService';
import { CharacterService } from './CharacterService';
import { start as startTweenHost } from '../rendering/TweenHostDriver';
import { start as startDOMInput } from '../systems/input/DOMInput';
import { GameHost, type GameHostConfig } from '../host/GameHost';
import { registerAllPixiFactories } from '../rendering/PixiFactoriesRegistry';
import { registerGlowTextures } from '../rendering/glowTextures';
import { AtmosphereLayer } from '../effects/atmosphere/AtmosphereLayer';
import { NatureLayer } from '../effects/nature/NatureLayer';
import { startCombatMoodDriver } from '../systems/atmosphere/CombatMoodDriver';
import { startBossMoodDriver } from '../systems/atmosphere/BossMoodDriver';
import { startTimeOfDayDriver } from '../systems/atmosphere/TimeOfDayDriver';
import { startChoreographyDriver } from '../systems/atmosphere/ChoreographyDriver';
import { startAtmosphereReactivityDriver } from '../systems/atmosphere/AtmosphereReactivityDriver';

export interface GameBootstrapOptions {
  containerElement: HTMLDivElement;
  player: Helix2000Character;
  authToken: string | null;
  initialScene: string;
  onGameCreated: (game: any) => void;
  onWorldCreated: (world: any) => void;
}

export class GameBootstrap {
  public static init(options: GameBootstrapOptions): any | null {
    const {
      containerElement,
      player,
      authToken,
      initialScene,
      onGameCreated,
      onWorldCreated: _onWorldCreated
    } = options;

    // Diagnostic state exposed on window for RenderingDiagnosticPanel
    const bootState = {
      initCalled: true,
      gameCreated: false,
      scenesAdded: false,
      readyFired: false,
      sceneStarted: false,
      progress: 0,
      stage: 'Initializing Engine...',
      error: null as string | null,
      abilityRegistration: null,
      timestamp: Date.now(),
    };
    (window as any).__GAME_BOOT__ = bootState;

    // Phase H10 Arc β: start the substrate-agnostic TweenHost rAF loop.
    // Idempotent — safe even if init() is called more than once.
    startTweenHost();
    // Phase H10 Arc ζ: start DOMInput global window listeners.
    startDOMInput();
    // Phase H10 Arc λ.1: wire every Pixi*.ts impl to its factory slot
    // BEFORE GameHost construction. Under default Phaser mode this is a
    // no-op (early-return). Under ?renderer=pixi, this is the load-bearing
    // call — pre-this-fix, factories silently fell back to Phaser because
    // pixiFactory was never registered.
    registerAllPixiFactories();
    // VFX-evolution M1: preload the shared glow-gradient textures so
    // effects/atmosphere can use them by key. Best-effort + async — never
    // blocks boot (effects fall back to graphics primitives until ready).
    void registerGlowTextures();
    // VFX-evolution M3: ambient atmosphere mounts per-zone on LEVEL_LOADED.
    AtmosphereLayer.install();
    // Tree & Nature system — biome-tinted vegetation mounts per-zone on
    // LEVEL_LOADED, behind the ISprite seam (lift-ready; see
    // src/game/effects/nature/SPEC.md). Composes with AtmosphereLayer: both
    // read the same resolveScene().biome so vegetation tracks the preset.
    NatureLayer.install();
    // Combat-reactive scene mood — cast heat pulses EVENTS.COMBAT_MOOD, which
    // AtmosphereLayer multiplies onto the level mood (reusable across maps).
    startCombatMoodDriver();
    // Boss-phase swell — boss phase transitions pulse EVENTS.BOSS_MOOD, which
    // AtmosphereLayer multiplies onto the mood (sceneMood × combatMood ×
    // bossMood). The undercroft dais intensifies as the Hollow Keeper
    // escalates, settles on defeat. Lane E S1.
    startBossMoodDriver();
    // Time-of-day — a slow global day/night clock drives EVENTS.SCENE_MOOD so
    // the ambiance breathes over real time (interiors/always-night maps opt out;
    // reduce-motion holds it calm). See docs/design/TIME_OF_DAY.md.
    startTimeOfDayDriver();
    // Set-piece choreography — region/event-triggered atmosphere timelines (the
    // "epic maker"). Reads scene.cues; composes with the mood bus + effect kit.
    // See src/game/systems/atmosphere/SPEC.md.
    startChoreographyDriver();
    // World-combat reactivity — positional, per-event-kind, per-layer-kind
    // pulses (fireflies scatter from impacts, godRays flare on holy casts).
    // Fourth driver in the CombatMoodDriver mold; envelopes always decay to 1.
    // See docs/plans/fx-grammar-specs/world-combat-reactivity.md.
    startAtmosphereReactivityDriver();

    gameLogger.info('game', '🔧 [GameBootstrap] init() called', {
      hasContainer: !!containerElement,
      hasPlayer: !!player,
      playerName: player?.name,
      authToken: authToken ? `${authToken.substring(0, 10)}...` : 'null',
      initialScene,
    });

    // Substrate-agnostic host config — only width/height/parent are used
    // (they size the hidden placeholder canvas). PixiOverlayHost owns the
    // actual rendering surface.
    const config: GameHostConfig = {
      width: DevConfig.game.width,
      height: DevConfig.game.height,
      parent: containerElement,
      backgroundColor: DevConfig.game.backgroundColor,
    };

    try {
      gameLogger.info('game', '🎮 [GameBootstrap] Creating GameHost...');
      // Boot through GameHost (IGameHost). Consumers hold the host reference;
      // it's also exposed on window.__GAME_HOST__ for diagnostics + the
      // boot-proof probes (verify-ptr, lobby-probe, etc.).
      const host = new GameHost(config);
      bootState.gameCreated = true;

      (window as any).__GAME_HOST__ = host;
      onGameCreated(host);

      gameLogger.info('game', '✅ [GameBootstrap] GameHost created, adding scenes...');

      // SceneRegistry-driven lifecycle: scenes registered with the
      // substrate-agnostic SceneRegistry and ticked by GameHost's rAF loop.
      // GameScene is loaded lazily after room bind to avoid a pre-room
      // v8.evaluateModule freeze from its large import tree.
      host.sceneRegistry.add('PreloadScene', PreloadScene);
      bootState.scenesAdded = true;
      bootState.readyFired = true;

      gameLogger.info('game', '✅ [GameBootstrap] PreloadScene added to SceneRegistry');

      try {
        if (initialScene === 'GalleryScene') {
          gameLogger.info('game', '🖼️ [GameBootstrap] Loading and starting GalleryScene');
          void import('../scenes/GalleryScene').then(({ GalleryScene }) => {
            host.sceneRegistry.add('GalleryScene', GalleryScene);
            host.scenes.start('GalleryScene');
          });
        } else {
          gameLogger.info('game', '🌍 [GameBootstrap] Starting PreloadScene with player data...');
          const initialLevelId = BootService.getInstance().getInitialLevelId(null);
          const initialSpawnId = BootService.getInstance().getInitialSpawnId();
          const characterService = CharacterService.getInstance();
          const dbChar = characterService.convertGameToDBCharacter(player);
          const startData = {
            token: authToken,
            characterId: player._id,
            characterData: dbChar,
            levelId: initialLevelId,
            spawnId: initialSpawnId,
            playerClass: player.class,
          };

          host.scenes.start('PreloadScene', startData);
          bootState.sceneStarted = true;
          gameLogger.info('game', `✅ [GameBootstrap] PreloadScene started with level: ${initialLevelId}`);
        }
      } catch (err) {
        bootState.error = String(err);
        gameLogger.error('game', '❌ [GameBootstrap] Failed to start scene:', { error: err });
      }

      return host;
    } catch (err) {
      bootState.error = String(err);
      gameLogger.error('game', '❌ [GameBootstrap] Failed to init GameHost:', { error: err });
      return null;
    }
  }
}
